import PDF from './pdf'
import { formatAccounting, capitalizeFirstLetter, getCurrentDateEnUsFormat } from '../lib/utils'

export default class Receipt extends PDF {
  /**
   * Description
   * @param {import('jspdf').DocumentProperties} args
   */
  constructor (args = {}) {
    super()
    this.doc.setProperties({
      unit: 'mm',
      format: 'a4',
      ...args,
    })
    this.addFonts()
  }

  /**
   * Description
   * @param {boolean} [cancel]
   */
  __header (cancel) {
    const title = !cancel ? `${capitalizeFirstLetter(this.getStoreName())} Report` : `Cancelled ${capitalizeFirstLetter(this.getStoreName())} Report`
    this.generateText({ fontSize: 24, text: title, coordinates: [65, 15], color: [55, 71, 79] })
    this.generateText({ fontSize: 12, text: getCurrentDateEnUsFormat(), coordinates: [100, 22] })
    this.doc.setFillColor('#CFD8DC')
    this.doc.line(15, 25, 195, 25, 'F')
  }

  /**
   * Description
   * @param {ReceiptData} subHeader
   * @returns {void}
   */
  __subHeader (subHeader) {
    const { name = '', paidAmount = 0, balance = 0, receiptNo = 'xxxxxxxx' } = subHeader || {}
    const [x, y] = [15, 40]
    const delimiter = ':'
    
    const { labelWidth: textLength } = this.generateField({ 
      label: 'Amount Paid', 
      value: formatAccounting(Number(paidAmount)), 
      coordinates: [x, y + 10], 
      delimiter, 
    })
    
    this.generateField({ 
      label: 'Receipt No.', 
      value: receiptNo, 
      coordinates: [x, y], 
      textLength, 
      delimiter, 
    })
    
    this.generateField({ 
      label: 'Customer', 
      value: name, 
      coordinates: [x, y + 5], 
      textLength, 
      delimiter, 
    })
    
    this.generateField({ 
      label: 'Balance', 
      value: formatAccounting(Number(balance)), 
      coordinates: [x, y + 15], 
      textLength, 
      delimiter, 
    })
  }

  /**
   * Description
   * @param {{ totalAmount: number; }} params
   * @param {ReceiptItem} body
   * @returns {any}
   */
  __table ({ totalAmount = 0 }, body) {
    this.doc.autoTable({
      head: [['Item', 'Qty', 'Price', 'Disc', 'Amount']],
      startY: 70,
      columnStyles: {
        amount: { halign: 'right' },
        price: { halign: 'right' },
      },
      body: [
        ...body,
        [{ content: 'Total', colSpan: 3, styles: { halign: 'left', fontStyle: 'bold' } }, { content: formatAccounting(Number(totalAmount)), styles: { halign: 'right' } }],
      ],
      columns: [
        { header: 'Item', dataKey: 'item' },
        { header: 'Qty', dataKey: 'quantity' },
        { header: 'Price', dataKey: 'price', styles: { halign: 'right' } },
        { header: 'Disc', datakey: 'discount', styles: { halign: 'right' } },
        { header: 'Amount', dataKey: 'amount', styles: { halign: 'right' } },
      ],
    })
  }

  /**
   * Description
   * @param {string} receiptNo
   * @param {boolean} [cancel]=false
   * @returns {void}
   */
  __save (receiptNo, cancel = false) {
    const name = !cancel ? `${this.getStoreName()}_receipt_${receiptNo}_${new Date().getTime()}` : `cancelled_${this.getStoreName()}_reciept_${receiptNo}_${new Date().getTime()}`
    this.doc.save(name)
  }

  /**
   * Description
   * @param {ReceiptData} data
   * @param {ReceiptItem[]} items
   * @param {boolean} [cancel]
   * @returns {any}
   */
  generate (data, items, cancel) {
    this.__header(cancel)
    this.__subHeader(data)
    this.__table(data, items)
    this.__save(data.receiptNo, cancel)
  }
}

/**
 * @typedef ReceiptData
 * @property {number} totalAmount
 * @property {string} name
 * @property {number} paidAmount
 * @property {string} receiptNo
 * 
 * @typedef ReceiptItem
 * @property {string} item
 * @property {number} quantity
 * @property {string} price
 * @property {string} discount
 * @property {string} amount
 */
